import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { IconButton, TableCell } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { isAfter } from "date-fns";
import { toast } from "react-toastify";
import LoopIcon from "@mui/icons-material/Loop";

// Models
import {
	IntegrationsLogFilterRequest,
	InsurerFilterRequest,
	BodyShopFilterRequest,
	SupplierFilterRequest,
} from "@/main/models/request";

// Redux & Api's
import { integrationLogApi, integrationApi } from "@/main/api";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getInsurers, resetReducerInsurers, useInsurerReducer } from "@/main/store/insurer";
import { getBodyshops, resetReducerBodyshops, useBodyshopReducer } from "@/main/store/bodyshop";
import { getSuppliers, resetReducerSuppliers, useSupplierReducer } from "@/main/store/supplier";
import {
	getIntegrationInterface,
	resetReducerIntegrationInterface,
	resetReducerService,
	useIntegrationTypeReducer,
	getService,
} from "@/main/store/integrationType";

// Utils & Enums
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { KeyValue } from "@/main/models/common";
import { getIntegrationsLogOptionsStatus } from "@/main/services/data/data";
import { formatDateToISOString, getDateNowTimeZoneUser, isTimePeriodGreater } from "@/main/services/utilsDate";
import {
	removeEmptyValues,
	getTranslateByKey,
	getDefaultMessageResultsFound,
	isNullUndefined,
	isValueQueryParams,
	mountReducerWithData,
} from "@/main/services/utils";
import { userIsBodyshop, userIsInsurer } from "@/main/services/utilsUser";
import { downloadPdfFile } from "@/main/services/utilsFile";
import { IntegrationsLogDataResponse } from "@/main/models/response";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { CustomError } from "@/main/services/class/customError";
import useFormQueryParams from "@/main/hooks/useformQueryParams";

// Components
import Button from "@/main/components/shared/button/button";
import Box from "@/main/components/shared/box";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import StatusIntegrationLog from "@/main/components/shared/status/statusIntegrationLog";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import KogKeyboardDateTimePicker from "@/main/components/shared/datetimepicker/keyboardDateTimePicker";
import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";
import TooltipComponent from "@/main/components/shared/tooltip/tooltipComponent";
import { withQueryparams } from "@/main/components/core/queryParams/queryparams";

// Styles
import { KOGTableRow } from "@/main/components/shared/boxGrid/styles";
import { AreaButton, KOGAreaButtons } from "@/main/components/shared/styles";
import * as S from "./styles";

export interface ISearchIntegrationsLogForm {
	country: KeyValue | null;
	startDate: Date;
	finalDate: Date;
	integrationType: KeyValue | null;
	service: KeyValue | null;
	interface: KeyValue | null;
	status: KeyValue | null;
	externalID: string;
	insurer: KeyValue | null;
	bodyshop: KeyValue | null;
	supplier: KeyValue | null;
	claimNumber: string;
}
interface IGridData {
	data: IntegrationsLogDataResponse[];
	totalItems: number;
	isLoading: boolean;
}

interface IPropsIntegrationsLogSearchGrid {
	gridData: IGridData;
	formFilters: IntegrationsLogFilterRequest | null;
	setFormFilters: (formFilters: IntegrationsLogFilterRequest | null) => void;
	getGridData: (formFilters: IntegrationsLogFilterRequest) => void;
	hasShowMore: boolean;
	pageIndex: number;
	setPageIndex: (pageIndex: number) => void;
	isLoadingInfiniteScroll: boolean;
}

const INITIAL_GRID_STATE: IGridData = {
	totalItems: 0,
	isLoading: false,
	data: [],
};

const useStylesTooltip = makeStyles({
	popperCustom: { marginLeft: 15 },
});

const IntegrationsLogSearchGrid = ({
	gridData,
	formFilters,
	setFormFilters,
	getGridData,
	hasShowMore,
	pageIndex,
	setPageIndex,
	isLoadingInfiniteScroll,
}: IPropsIntegrationsLogSearchGrid) => {
	const classesTooltip = useStylesTooltip();
	const rowHeaders = [
		getTranslateByKey("information.id"),
		getTranslateByKey("information.integrationType"),
		getTranslateByKey("information.service"),
		getTranslateByKey("information.interface"),
		getTranslateByKey("information.orderNr"),
		getTranslateByKey("information.claimNumber"),
		getTranslateByKey("information.logDateHour"),
		getTranslateByKey("information.messages"),
		getTranslateByKey("information.externalID"),
		getTranslateByKey("information.buyer"),
		getTranslateByKey("information.supplier"),
		getTranslateByKey("information.request"),
		getTranslateByKey("information.response"),
		getTranslateByKey("information.status"),
	];
	const titleGrid = getDefaultMessageResultsFound(gridData.data.length, gridData.totalItems);

	function onLoadSearchGrid() {
		const index = pageIndex + PaginationEnum.PageIndexIntial;
		setPageIndex(index);

		const formFiltersOnLoadSearchGrid = formFilters;
		if (formFiltersOnLoadSearchGrid) {
			formFiltersOnLoadSearchGrid.pageIndex = index;

			setFormFilters(formFiltersOnLoadSearchGrid);
			getGridData(formFiltersOnLoadSearchGrid);
		}
	}

	function handleDownloadFile(idFile: string, idSystemApplication: number) {
		integrationLogApi.getFile(String(idFile), idSystemApplication).then((response) => {
			downloadPdfFile(
				response.data.result.file,
				`tag${response.data.result.fileName}.${response.data.result.fileExtension}`,
			);
		});
	}

	function renderText(text: string | number | undefined, hasTooltip: boolean = false) {
		if (!text) return "-";

		if (hasTooltip) {
			return <S.TooltipText title={text as string}>{text}</S.TooltipText>;
		}
		return text;
	}

	if (gridData.isLoading) return <LoadingComponent />;
	if (gridData.data.length === 0 && formFilters !== null) return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
	if (gridData.data.length === 0 && !gridData.isLoading) return null;

	return (
		<BoxGrid
			title={titleGrid}
			rowHeaders={rowHeaders}
			infiniteScrollConfig={{
				dataLength: gridData.data.length,
				fetchData: () => onLoadSearchGrid(),
				hasMore: hasShowMore,
				isLoading: isLoadingInfiniteScroll,
			}}
		>
			{gridData.data.map((log) => (
				<KOGTableRow key={log.integrationLogGuid} style={{ cursor: "auto" }}>
					<TableCell style={{ padding: "5px 15px" }}>
						<S.StyledTableCell>{renderText(log.integrationLogGuid, true)}</S.StyledTableCell>
					</TableCell>
					<TableCell style={{ padding: "5px 15px" }}>{log.integrationTypeName}</TableCell>
					<TableCell style={{ padding: "5px 15px" }}>{log.integrationName}</TableCell>
					<TableCell style={{ padding: "5px 15px" }}>{log.integrationInterfaceName}</TableCell>
					<TableCell style={{ padding: "5px 15px" }}>{log.orderNumber ? log.orderNumber : "-"}</TableCell>
					<TableCell style={{ padding: "5px 15px" }}>{log.claimNumber ? log.claimNumber : "-"}</TableCell>
					<TableCell style={{ padding: "5px 15px" }}>{log.logDateFormated}</TableCell>
					<TableCell align="center" style={{ padding: "5px 15px" }}>
						<S.StyledTableCell>{renderText(log.errorMessage, true)}</S.StyledTableCell>
					</TableCell>
					<TableCell style={{ padding: "5px 15px", wordBreak: "break-all" }}>{log.wan ? log.wan : "-"}</TableCell>
					<TableCell style={{ padding: "5px 15px", wordBreak: "break-all" }}>{log.buyerName}</TableCell>
					<TableCell
						align={log.supplierName ? "left" : "center"}
						style={{ padding: "5px 15px", wordBreak: "break-all" }}
					>
						{log.supplierName}
					</TableCell>
					<TableCell align="center">
						{log.idRequest && log.idRequest !== "" && (
							<TooltipComponent
								title={`${getTranslateByKey("information.downloadFile")}`}
								placement="top"
								stylePopper={classesTooltip.popperCustom}
							>
								<S.IconButtonFile
									className="ignore-click"
									onClick={() => handleDownloadFile(log?.idRequest, log?.idSystemApplication)}
								>
									<S.IconButtonFile fontSize="medium" />
								</S.IconButtonFile>
							</TooltipComponent>
						)}
					</TableCell>
					<TableCell align="center">
						{log.idResponse && log.idResponse !== "" && (
							<TooltipComponent
								title={`${getTranslateByKey("information.downloadFile")}`}
								placement="top"
								stylePopper={classesTooltip.popperCustom}
							>
								<S.IconButtonFile
									className="ignore-click"
									onClick={() => handleDownloadFile(log?.idResponse, log?.idSystemApplication)}
								>
									<S.IconButtonFile fontSize="medium" />
								</S.IconButtonFile>
							</TooltipComponent>
						)}
					</TableCell>
					<TableCell>
						<StatusIntegrationLog status={Number(log?.idStatus)} />
					</TableCell>
				</KOGTableRow>
			))}
		</BoxGrid>
	);
};

function IntegrationsLogScreen() {
	// Hooks & reducers
	const dispatch = useDispatch();
	const countryReducer = useCountryReducer();
	const insurerReducer = useInsurerReducer();
	const bodyshopReducer = useBodyshopReducer();
	const supplierReducer = useSupplierReducer();
	const statusIntegrationsLog = getIntegrationsLogOptionsStatus();
	const { service: serviceReducer, integrationInterface: intergrationInterfaceReducer } = useIntegrationTypeReducer();

	const optionDefaultCountryDrop =
		countryReducer.countriesUser.data !== null && countryReducer.countriesUser.data.length === 1
			? countryReducer.countriesUser.data[0]
			: null;

	const optionDefaultInsurerDrop =
		insurerReducer.insurers.data !== null && insurerReducer.insurers.data.length === 1
			? insurerReducer.insurers.data[0]
			: null;

	const optionDefaultBodyshopDrop =
		bodyshopReducer.bodyshops.data !== null && bodyshopReducer.bodyshops.data.length === 1
			? bodyshopReducer.bodyshops.data[0]
			: null;

	const optionDefaultSupplierDrop =
		supplierReducer.suppliers.data !== null && supplierReducer.suppliers.data.length === 1
			? supplierReducer.suppliers.data[0]
			: null;

	const optionDefaultServiceDrop =
		serviceReducer.data !== null && serviceReducer.data.length === 1 ? serviceReducer.data[0] : null;

	// States
	const INITIAL_FORM_STATE: ISearchIntegrationsLogForm = {
		country: null,
		startDate: getDateNowTimeZoneUser(),
		finalDate: getDateNowTimeZoneUser(),
		integrationType: null,
		service: null,
		status: null,
		interface: null,
		externalID: "",
		insurer: null,
		bodyshop: null,
		supplier: null,
		claimNumber: "",
	};
	const [hasShowMore, setHasShowMore] = useState(false);
	const [showFilters, setShowFilters] = useState(true);
	const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
	const [formFilters, setFormFilters] = useState<IntegrationsLogFilterRequest | null>(null);
	const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
	const [integrationTypeData, setIntegrationTypeData] = useState<{
		data: KeyValue[];
		isLoading: boolean;
		isError: boolean;
		errorMessages: any;
	}>({
		data: [],
		isLoading: false,
		errorMessages: [],
		isError: false,
	});

	const {
		control,
		getValues,
		setValue,
		formState: { errors },
		reset,
		queryParamsValues,
		customHandleSubmit,
		watch,
	} = useFormQueryParams<ISearchIntegrationsLogForm>({
		defaultValues: INITIAL_FORM_STATE,
		onSubmit: onSubmit,
		mapDefaultValues: {
			country: countryReducer.countriesUser,
			integrationType: integrationTypeData,
			service: serviceReducer,
			status: mountReducerWithData(statusIntegrationsLog),
			interface: intergrationInterfaceReducer,
			insurer: insurerReducer.insurers,
			bodyshop: bodyshopReducer.bodyshops,
			supplier: supplierReducer.suppliers,
		},
	});

	const [changeStartDate, setChangeStartDate] = useState<Date | number>(0);
	const [changeFinalDate, setChangeFinalDate] = useState<Date | number>(0);
	const isUserInsurerLogged = userIsInsurer();
	const isUserBodyShopLogged = userIsBodyshop();

	const watcherCountry = watch("country");
	const watcherInsurer = watch("insurer");
	const watcherBodyshop = watch("bodyshop");
	const watcherIntegrationType = watch("integrationType");
	const watcherService = watch("service");
	const watcherStartDate = watch("startDate");
	const watcherFinalDate = watch("finalDate");
	const HOURS_RANGE_DEFAULT = 24;

	const isDisabledSupplierDrop = () => supplierReducer.suppliers.data === null || getValues().country === null;

	const disabledIntegrationInterface = isNullUndefined(watcherService);

	function handleFilters() {
		setShowFilters(!showFilters);
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	function callInsurerApiDomain(companySelected: KeyValue | null = null) {
		// Clean fields & reducers
		if (!isValueQueryParams(companySelected)) {
			dispatch(resetReducerInsurers());
		}

		if (watcherCountry !== null) {
			const request: InsurerFilterRequest = {
				...(companySelected && { guidBodyShop: String(companySelected.key) }),
				guidCountry: String(watcherCountry?.key),
				type: HeadQuarterBranchFilterEnum.Only_Headquarters,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getInsurers(request));
		}
	}

	function callBodyshopApiDomain(insurerSelected: KeyValue | null = null) {
		// Clean fields & reducers
		if (!isValueQueryParams(insurerSelected)) {
			dispatch(resetReducerBodyshops());
		}

		if (watcherCountry !== null) {
			const request: BodyShopFilterRequest = {
				...(insurerSelected && { guidInsurer: String(insurerSelected.key) }),
				guidCountry: String(watcherCountry?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getBodyshops(request));
		}
	}

	function callSupplierApiDomain(optionSelected: KeyValue | null) {
		if (!isValueQueryParams(optionSelected)) {
			setValue("supplier", null);
			dispatch(resetReducerSuppliers());
		}

		if (optionSelected !== null) {
			const request: SupplierFilterRequest = {
				guidCountry: String(optionSelected.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getSuppliers(request));
		}
	}

	function callServiceApiDomain(optionSelected: KeyValue | null) {
		const isOptionQueryParams = !isValueQueryParams(optionSelected);
		if (isOptionQueryParams) {
			setValue("service", null);
			setValue("interface", null);
			dispatch(resetReducerService());
			dispatch(resetReducerIntegrationInterface());
		}

		if (optionSelected !== null) {
			const idIntegrationType = Number(optionSelected.key);
			dispatch(getService(idIntegrationType));
		} else {
			setValue("service", null);
			setValue("interface", null);
			// Get services withot filter integration type
			dispatch(getService(0));
		}
	}

	async function callIntegrationInterface(service: KeyValue | null, idIntegrationType: number) {
		const isOptionQueryParams = isNullUndefined(service) || !isValueQueryParams(service);
		if (isOptionQueryParams) {
			setValue("interface", null);
			dispatch(resetReducerIntegrationInterface());
		}

		if (service === null) return;
		dispatch(
			getIntegrationInterface({
				idIntegrationType: idIntegrationType,
				guidIntegration: String(service.key),
			}),
		);
	}

	function existsInDrop(data: KeyValue[], nameField: any) {
		const fieldKeySelected = getValues(nameField)?.key;
		const existsInData = data.find((item) => item.key === fieldKeySelected);

		if (!existsInData) {
			setValue(nameField, null);
		}
	}

	function handleClear() {
		reset(INITIAL_FORM_STATE);
		setValue("country", optionDefaultCountryDrop);
		setValue("status", statusIntegrationsLog[0]);
		setValue("insurer", null);
		setValue("bodyshop", null);
		setGridData(INITIAL_GRID_STATE);
		setFormFilters(null);
		setPageIndex(PaginationEnum.PageIndexIntial);
		setHasShowMore(false);
		setIsLoadingInfiniteScroll(false);
		onLoadDateRangePicker();
		dispatch(resetReducerService());
		// Get services withot filter integration type
		dispatch(getService(0));

		if (optionDefaultCountryDrop) {
			callBodyshopApiDomain();
			callInsurerApiDomain();
			callSupplierApiDomain(optionDefaultCountryDrop);
		} else {
			dispatch(resetReducerBodyshops());
			dispatch(resetReducerInsurers());
			dispatch(resetReducerSuppliers());
		}
	}

	function validateForm() {
		const messages = [];

		if (changeStartDate === null || String(changeStartDate) === "Invalid Date")
			messages.push(getTranslateByKey("information.startDateSinceIsRequired", true));

		if (changeFinalDate === null || String(changeFinalDate) === "Invalid Date")
			messages.push(getTranslateByKey("information.finalDateUntilIsRequired", true));

		if (changeStartDate && isAfter(changeFinalDate, getDateNowTimeZoneUser())) {
			messages.push(getTranslateByKey("information.theDateHourUntilMustNotBeLaterThanTheCurrentHour", true));
		}

		if (changeStartDate && isAfter(changeStartDate, changeFinalDate)) {
			messages.push(getTranslateByKey("information.theStartDateMustNotBeGreaterThanTheFinalDate", true));
		} else {
			if (isTimePeriodGreater(watcherStartDate, watcherFinalDate, HOURS_RANGE_DEFAULT))
				messages.push(getTranslateByKey("information.theRangeDateMustBeBetween24Hours", true));
		}

		if (messages.length > 0) {
			messages.map((message) => toast.error(message));
			throw new CustomError(messages.join("/n"), "MISSING_FIELDS");
		}
		return true;
	}

	function onSubmit(formInputs: ISearchIntegrationsLogForm) {
		validateForm();

		setPageIndex(PaginationEnum.PageIndexIntial);
		setGridData({
			isLoading: true,
			totalItems: 0,
			data: [],
		});

		const requestForm: IntegrationsLogFilterRequest = {
			guidCountry: String(formInputs.country?.key),
			startDate: formatDateToISOString(formInputs.startDate),
			endDate: formatDateToISOString(formInputs.finalDate),
			guidIntegration: String(formInputs.service?.key),
			guidIntegrationInterface: String(formInputs.interface?.key),
			integrationType:
				!isNullUndefined(formInputs.integrationType) && !isNullUndefined(formInputs.integrationType.key)
					? Number(formInputs.integrationType.key)
					: null,
			integrationStatus: formInputs.status?.key ? Number(formInputs.status.key) : null,
			wan: formInputs?.externalID ? String(formInputs.externalID) : null,
			guidBodyshop: formInputs.bodyshop?.key ? String(formInputs.bodyshop.key) : null,
			guidInsurer: formInputs.insurer?.key ? String(formInputs.insurer.key) : null,
			guidSupplier: formInputs.supplier?.key ? String(formInputs.supplier.key) : null,
			claimNumber: formInputs?.claimNumber ? String(formInputs.claimNumber) : null,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
		};

		setFormFilters(requestForm);
		const requestFormatted = removeEmptyValues(requestForm);
		integrationLogApi.getSearchGrid(requestFormatted).then((response) => {
			setGridData({
				isLoading: false,
				totalItems: response.data.totalNumberOfRecords,
				data: response.data?.results ? response.data.results : [],
			});
			setHasShowMore(response.data.results.length < response.data.totalNumberOfRecords);
		});
		setShowFilters(!showFilters);
	}

	function onLoadInfiniteScroll(request: IntegrationsLogFilterRequest) {
		setIsLoadingInfiniteScroll(true);
		integrationLogApi
			.getSearchGrid(request)
			.then((response) => {
				let resultData: IntegrationsLogDataResponse[] = [];
				let resultTotalItems: number = 0;
				if (response.data && response.data.results && response.data.results.length > 0) {
					resultData = response.data.results;
					resultTotalItems = response.data.totalNumberOfRecords;
				}
				if (gridData.data.length > 0) {
					resultData = [...gridData.data, ...resultData];
				}
				setGridData({
					isLoading: false,
					totalItems: resultTotalItems,
					data: resultData,
				});

				if (resultTotalItems < PaginationEnum.PageSize30 || resultTotalItems === resultData.length) {
					setHasShowMore(false);
				}
			})
			.finally(() => setIsLoadingInfiniteScroll(false));
	}

	function initalDateTimeForm(intervalInHours: number | null = null) {
		const dateNowTimeZoneUser = getDateNowTimeZoneUser();

		if (intervalInHours) {
			dateNowTimeZoneUser.setHours(dateNowTimeZoneUser.getHours() - intervalInHours);
		}

		return dateNowTimeZoneUser;
	}

	function onLoadDateRangePicker(startDate?: Date, finalDate?: Date) {
		const firstDateResult = startDate || initalDateTimeForm(HOURS_RANGE_DEFAULT);
		const secondDateResult = finalDate || initalDateTimeForm();

		setValue("startDate", firstDateResult);
		setValue("finalDate", secondDateResult);
		setChangeStartDate(firstDateResult);
		setChangeFinalDate(secondDateResult);
	}

	function loadIntegrationLog() {
		setIntegrationTypeData({
			data: [],
			isLoading: true,
			errorMessages: [],
			isError: false,
		});
		integrationApi
			.getDomainIntegrationType()
			.then((result) => {
				setIntegrationTypeData({
					data: result.data.result,
					isLoading: false,
					errorMessages: [],
					isError: false,
				});
			})
			.catch(() => {
				setIntegrationTypeData({
					data: [],
					isLoading: false,
					errorMessages: [],
					isError: false,
				});
			});
	}

	useEffect(() => {
		if (isNullUndefined(queryParamsValues)) return;
		onLoadDateRangePicker(queryParamsValues.startDate, queryParamsValues.finalDate);
	}, [JSON.stringify(queryParamsValues)]);

	useEffect(() => {
		dispatch(getCountriesUser());
		loadIntegrationLog();
		// Get services withot filter integration type
		dispatch(getService(0));

		return () => {
			dispatch(resetReducerBodyshops());
			dispatch(resetReducerInsurers());
			dispatch(resetReducerSuppliers());
			dispatch(resetReducerIntegrationInterface());
			dispatch(resetReducerService());
		};
	}, []);

	useEffect(() => {
		if (watcherCountry === null) return;
		callSupplierApiDomain(watcherCountry);
	}, [JSON.stringify(watcherCountry?.key)]);

	useEffect(() => {
		callBodyshopApiDomain(watcherInsurer);
	}, [JSON.stringify(watcherInsurer?.key), JSON.stringify(watcherCountry?.key)]);

	useEffect(() => {
		callInsurerApiDomain(watcherBodyshop);
	}, [JSON.stringify(watcherBodyshop?.key), JSON.stringify(watcherCountry?.key)]);

	useEffect(() => {
		if (insurerReducer.insurers.data !== null && !insurerReducer.insurers.isLoading) {
			const isTypeCompanyUserInsurer = userIsInsurer();
			existsInDrop(insurerReducer.insurers.data, "insurer");
			if (isTypeCompanyUserInsurer && optionDefaultInsurerDrop) callBodyshopApiDomain(optionDefaultInsurerDrop);
		}
	}, [insurerReducer.insurers.data, insurerReducer.insurers.isLoading]);

	useEffect(() => {
		if (bodyshopReducer.bodyshops.data && !bodyshopReducer.bodyshops.isLoading) {
			const isTypeCompanyUserBodyshop = userIsBodyshop();
			existsInDrop(bodyshopReducer.bodyshops.data, "bodyshop");
			if (isTypeCompanyUserBodyshop && optionDefaultBodyshopDrop) callInsurerApiDomain(optionDefaultBodyshopDrop);
		}
	}, [bodyshopReducer.bodyshops.data, bodyshopReducer.bodyshops.isLoading]);

	useEffect(() => {
		if (watcherIntegrationType === null || integrationTypeData.isLoading) return;
		if (watcherIntegrationType?.value === "") {
			const matchingIntegrationType = integrationTypeData?.data.find((type) => type.key === watcherIntegrationType.key);

			if (matchingIntegrationType) {
				setValue("integrationType", {
					key: watcherIntegrationType.key,
					value: matchingIntegrationType.value,
				});
			}
		}
	}, [JSON.stringify(watcherIntegrationType?.key), JSON.stringify(integrationTypeData.isLoading)]);

	useEffect(() => {
		const guidIntegrationTypeQueryParams = queryParamsValues?.integrationType?.key;
		const guidServiceQueryParams = queryParamsValues?.service?.key;
		if (!isNullUndefined(guidIntegrationTypeQueryParams)) {
			callServiceApiDomain({ key: guidIntegrationTypeQueryParams, value: "" });
		}
		if (!isNullUndefined(guidServiceQueryParams)) {
			const idIntegrationType = !isNullUndefined(guidIntegrationTypeQueryParams)
				? Number(guidIntegrationTypeQueryParams)
				: 0;
			callIntegrationInterface(
				{
					key: guidServiceQueryParams,
					value: "",
				},
				idIntegrationType,
			);
		}
	}, [JSON.stringify(queryParamsValues)]);

	const disabledBodyshop = bodyshopReducer.bodyshops.data === null || isUserBodyShopLogged;
	const disabledInsurer = isUserInsurerLogged;

	return (
		<ContainerScreen
			hasBackground
			title={`
      ${getTranslateByKey("module.integration")} ${" > "}
        ${getTranslateByKey("screen.integrationsLog")}
    `}
		>
			<S.Container>
				<AreaButton>
					<Button onClick={handleFilters} id="id-searchIntegrationsLog-btn-filters">
						{getTranslateByKey("information.filters")}
					</Button>
				</AreaButton>
				<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
					<form onSubmit={customHandleSubmit} noValidate>
						<S.RowInputs>
							<KogAutoCompleteControlled
								label={getTranslateByKey("information.country")}
								nameCustom="country"
								control={control}
								options={countryReducer.countriesUser.data}
								isLoading={countryReducer.countriesUser.isLoading}
								rulesCustom={{ required: true }}
								hasErrorCustom={!!errors.country}
								errorMessageCustom={
									errors.country?.type === "required" ? getTranslateByKey("information.countryRequired", true) : ""
								}
								onChangeCustom={(optionSelected: KeyValue | null) => {
									callSupplierApiDomain(optionSelected);
								}}
								autoHighlightCustom={!!optionDefaultCountryDrop}
								setValueCustom={setValue}
								disabled={optionDefaultCountryDrop !== null}
								id="id-searchIntegrationsLog-select-country"
							/>
							<S.DateContainer>
								<KogKeyboardDateTimePicker
									name="startDate"
									label={getTranslateByKey("information.dateHourSince")}
									value={watcherStartDate}
									error={
										changeStartDate === null ||
										String(changeStartDate) === "Invalid Date" ||
										isTimePeriodGreater(watcherStartDate, watcherFinalDate, HOURS_RANGE_DEFAULT) ||
										isAfter(changeStartDate, changeFinalDate) ||
										!!errors.startDate
									}
									onChange={(date) => {
										setChangeStartDate(date);
										if (date instanceof Date) {
											setValue("startDate", date);
										}
									}}
									id="id-searchIntegrationsLog-select-dateHourSince"
								/>
								<KogKeyboardDateTimePicker
									name="finalDate"
									label={getTranslateByKey("information.dateHourUntil")}
									value={watcherFinalDate}
									error={
										changeFinalDate === null ||
										String(changeFinalDate) === "Invalid Date" ||
										isTimePeriodGreater(watcherStartDate, watcherFinalDate, HOURS_RANGE_DEFAULT) ||
										isAfter(changeStartDate, changeFinalDate) ||
										!!errors.finalDate ||
										isAfter(changeFinalDate, getDateNowTimeZoneUser())
									}
									onChange={(date) => {
										setChangeFinalDate(date);
										if (date instanceof Date) {
											setValue("finalDate", date);
										}
									}}
									id="id-searchIntegrationsLog-select-dateHourUntil"
								/>
								<IconButton
									onClick={() => {
										onLoadDateRangePicker();
									}}
									title={getTranslateByKey("information.resetDate")}
									id="id-searchIntegrationsLog-btn-resetDate"
									sx={{ margin: "0 10px" }}
								>
									<LoopIcon />
								</IconButton>
							</S.DateContainer>
							<KogAutoCompleteControlled
								label={getTranslateByKey("information.integrationType")}
								control={control}
								nameCustom="integrationType"
								options={integrationTypeData.data}
								isLoading={integrationTypeData.isLoading}
								onChangeCustom={(optionSelected: KeyValue | null) => {
									callServiceApiDomain(optionSelected);
								}}
								disabled={integrationTypeData.data === null}
								setValueCustom={setValue}
								id="id-searchIntegrationsLog-select-integrationType"
							/>
							<KogAutoCompleteControlled
								required
								label={getTranslateByKey("information.service")}
								control={control}
								options={serviceReducer.data}
								isLoading={serviceReducer.isLoading}
								nameCustom="service"
								rulesCustom={{ required: true }}
								errorMessageCustom={
									errors.service?.type === "required" ? getTranslateByKey("information.serviceRequired", true) : ""
								}
								hasErrorCustom={!!errors.service}
								onChangeCustom={(optionSelected: KeyValue | null) => {
									const idIntegrationType =
										!isNullUndefined(watcherIntegrationType) && !isNullUndefined(watcherIntegrationType.key)
											? Number(watcherIntegrationType.key)
											: 0;
									callIntegrationInterface(optionSelected, idIntegrationType);
								}}
								autoHighlightCustom={!!optionDefaultServiceDrop}
								setValueCustom={setValue}
								disabled={optionDefaultServiceDrop !== null}
								id="id-searchIntegrationsLog-select-service"
							/>
							<KogAutoCompleteControlled
								id="id-searchIntegration-select-interface"
								nameCustom="interface"
								label={getTranslateByKey("information.interface")}
								rulesCustom={{ required: true }}
								isDefaultValueUnique
								control={control}
								setValueCustom={setValue}
								options={intergrationInterfaceReducer.data}
								isLoading={intergrationInterfaceReducer.isLoading}
								disabled={disabledIntegrationInterface}
								onChangeCustom={() => {}}
							/>
							<KogAutoCompleteControlled
								label={getTranslateByKey("information.status")}
								control={control}
								nameCustom="status"
								rulesCustom={{ required: true }}
								hasErrorCustom={!!errors.status}
								errorMessageCustom={
									errors.status?.type === "required" ? getTranslateByKey("information.statusRequired", true) : ""
								}
								options={statusIntegrationsLog}
								valueDefaultKey={statusIntegrationsLog[0].key}
								onChangeCustom={() => {}}
								setValueCustom={setValue}
								id="id-searchIntegrationsLog-select-status"
							/>
						</S.RowInputs>
						<S.RowInputs>
							<KogInputTextControlledNew
								id="id-searchIntegrationsLog-text-externalID"
								name="externalID"
								label={getTranslateByKey("information.externalID")}
								control={control}
							/>
							<KogAutoCompleteControlled
								id="id-searchIntegrationsLog-select-insurer"
								label={getTranslateByKey("information.insurer")}
								nameCustom="insurer"
								setValueCustom={setValue}
								control={control}
								rulesCustom={{ required: false }}
								errorMessageCustom=""
								hasErrorCustom={false}
								options={insurerReducer.insurers.data}
								isLoading={insurerReducer.insurers.isLoading}
								onChangeCustom={(optionSelected: KeyValue | null) => {
									callBodyshopApiDomain(optionSelected);
								}}
								autoHighlightCustom={!!optionDefaultInsurerDrop}
								disabled={disabledInsurer || !!optionDefaultInsurerDrop}
							/>
							<KogAutoCompleteControlled
								id="id-searchIntegrationsLog-select-bodyshop"
								label={getTranslateByKey("information.bodyshop")}
								nameCustom="bodyshop"
								setValueCustom={setValue}
								control={control}
								rulesCustom={{ required: false }}
								options={bodyshopReducer.bodyshops.data}
								isLoading={bodyshopReducer.bodyshops.isLoading}
								onChangeCustom={(optionSelected: KeyValue | null) => {
									callInsurerApiDomain(optionSelected);
								}}
								errorMessageCustom=""
								disabled={disabledBodyshop}
							/>
							<KogAutoCompleteControlled
								label={getTranslateByKey("information.supplier")}
								control={control}
								nameCustom="supplier"
								rulesCustom={{ required: false }}
								errorMessageCustom=""
								hasErrorCustom={false}
								options={supplierReducer.suppliers.data}
								isLoading={supplierReducer.suppliers.isLoading}
								onChangeCustom={() => {}}
								disabled={isDisabledSupplierDrop()}
								autoHighlightCustom={!!optionDefaultSupplierDrop}
								setValueCustom={setValue}
								id="id-searchIntegrationsLog-select-supplier"
							/>
							<KogInputTextControlledNew
								id="id-searchIntegrationsLog-text-claimNumber"
								name="claimNumber"
								label={getTranslateByKey("information.claimNumber")}
								control={control}
							/>
						</S.RowInputs>
						<KOGAreaButtons>
							<SecondaryButton id="id-searchIntegrationsLog-btn-search" type="submit">
								{getTranslateByKey("information.search")}
							</SecondaryButton>
							<SecondaryButton onClick={handleClear} id="id-searchIntegrationsLog-btn-clear">
								{getTranslateByKey("information.clear")}
							</SecondaryButton>
						</KOGAreaButtons>
					</form>
				</Box>

				<IntegrationsLogSearchGrid
					isLoadingInfiniteScroll={isLoadingInfiniteScroll}
					hasShowMore={hasShowMore}
					pageIndex={pageIndex}
					getGridData={(request: IntegrationsLogFilterRequest) => onLoadInfiniteScroll(request)}
					setPageIndex={setPageIndex}
					gridData={gridData}
					formFilters={formFilters}
					setFormFilters={setFormFilters}
				/>
			</S.Container>
		</ContainerScreen>
	);
}

export default withQueryparams(IntegrationsLogScreen);
